import { Card, EmptyState, Skeleton } from '../../ui'
import { Kpi, inr, when, useAdmin } from './shared'
import styles from './admin.module.css'

interface AdminBilling {
  mrr_paise: number; active_paid: number; past_due: number; trialing: number; churned_30d: number; churn_30d_pct: number; trials_not_converted_30d: number
  failed_payments_30d: number; collected_30d_paise: number; configured: boolean
  by_plan: Array<{ plan_code: string; billing_period: string; status: string; n: number }>
  failed_payments: Array<{ id: string; email: string | null; amount_paise: number; error_description: string | null; created_at: string }>
}

export function BillingTab() {
  const { data: m, error } = useAdmin<AdminBilling>('/admin/billing')
  if (error && !m) return <Card variant="flush"><EmptyState kind="error" title="Could not load billing" compact>{error}</EmptyState></Card>
  if (!m) return <Card><Skeleton lines={3} label="Loading billing metrics" /></Card>
  return (
    <div className={styles.section}>
      {!m.configured && <p className={`${styles.notice} ${styles.noticeWarn}`}>Razorpay is not configured yet — checkout is closed and all numbers below stay at zero until keys are added.</p>}
      <section aria-label="Subscription metrics" className={styles.kpis}>
        <Kpi label="MRR (excl. GST)" value={inr(m.mrr_paise)} hint="yearly plans counted as ÷12" />
        <Kpi label="Active paid" value={m.active_paid} hint={m.past_due ? `${m.past_due} retrying payment` : undefined} />
        <Kpi label="In trial" value={m.trialing} />
        <Kpi label="Churn (30 days)" value={`${m.churn_30d_pct}%`} hint={`${m.churned_30d} paid ended · ${m.trials_not_converted_30d} trials not converted`} />
        <Kpi label="Failed payments (30 days)" value={m.failed_payments_30d} />
        <Kpi label="Collected (30 days, incl. GST)" value={inr(m.collected_30d_paise)} />
      </section>
      <div className={styles.grid2}>
        <Card title="Subscriptions by plan" variant="flush">
          {m.by_plan.length === 0 ? <EmptyState title="No subscriptions" compact /> : (
            <table className={styles.table}><caption className="sr-only">Subscriptions by plan</caption>
              <thead><tr><th scope="col">Plan</th><th scope="col">Period</th><th scope="col">Status</th><th scope="col" className={styles.thNum}>Users</th></tr></thead>
              <tbody>{m.by_plan.map((p, i) => <tr key={i}><td>{p.plan_code}</td><td>{p.billing_period ?? '—'}</td><td>{p.status}</td><td className={styles.num}>{p.n}</td></tr>)}</tbody>
            </table>
          )}
        </Card>
        <Card title="Recent failed payments" variant="flush">
          {m.failed_payments.length === 0 ? <EmptyState title="No failed payments" compact /> : (
            <table className={styles.table}><caption className="sr-only">Failed payments</caption>
              <thead><tr><th scope="col">When</th><th scope="col">User</th><th scope="col" className={styles.thNum}>Amount</th><th scope="col">Reason</th></tr></thead>
              <tbody>{m.failed_payments.map(f => <tr key={f.id}><td className={styles.muted}>{when(f.created_at)}</td><td>{f.email ?? '—'}</td><td className={styles.num}>{inr(f.amount_paise)}</td><td>{f.error_description ?? '—'}</td></tr>)}</tbody>
            </table>
          )}
        </Card>
      </div>
    </div>
  )
}
